import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Filter, Loader2 } from "lucide-react";
import { api, formatError } from "../lib/api";
import { columnLabel } from "../config/databaseFilters";
import { broadcastDbFiltersChanged } from "../lib/dbFilterBus";

const MAX = 5;

// Admin picker for which columns appear as filters on a database page.
export function DbFilterConfigPanel({ slug, reloadKey, setupPrompt, onSaved }) {
  const [columns, setColumns] = useState([]);
  const [saved, setSaved] = useState(null);
  const [picked, setPicked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get(`/databases/${slug}/filter-config`).then(({ data }) => {
      const cols = data.columns || [];
      const configured = Array.isArray(data.filter_columns);
      const initial = configured ? data.filter_columns : [];
      setColumns(cols);
      setSaved(configured ? data.filter_columns : null);
      setPicked(initial.filter((c) => cols.includes(c)).slice(0, MAX));
    }).catch((e) => toast.error(formatError(e))).finally(() => setLoading(false));
  }, [slug, reloadKey]);

  const dirty = useMemo(
    () => saved === null || JSON.stringify(saved) !== JSON.stringify(picked),
    [saved, picked],
  );

  const toggle = (col) => {
    if (picked.includes(col)) { setPicked(picked.filter((c) => c !== col)); return; }
    if (picked.length >= MAX) {
      toast.error(`You can select a maximum of ${MAX} filters per database.`, { id: "db-filter-max" });
      return;
    }
    setPicked([...picked, col]);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/databases/${slug}/filter-config`, { filter_columns: picked });
      setSaved(data.filter_columns);
      broadcastDbFiltersChanged(slug);
      toast.success("Filters saved — the database page updates immediately.");
      onSaved?.();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <section
      className={`rounded-2xl border p-4 sm:p-5 ${setupPrompt ? "border-amber-400 bg-amber-50/60" : "border-border bg-white"}`}
      data-testid="admin-db-filter-config"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-sm inline-flex items-center gap-2">
          <Filter className="w-4 h-4" /> Page filters
        </h3>
        <span className="text-xs text-muted-foreground" data-testid="admin-db-filter-count">
          {picked.length} / {MAX} selected
        </span>
      </div>
      {setupPrompt && (
        <p className="mt-2 text-xs text-amber-900" data-testid="admin-db-filter-setup-prompt">
          New dataset uploaded — choose which columns members can filter by, then save.
        </p>
      )}
      <p className="mt-1 text-xs text-muted-foreground">
        Pick up to {MAX} columns to show as filters on this database's page. Columns with a few
        distinct values become dropdowns; others become “contains” search boxes.
      </p>
      {loading ? (
        <p className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading columns…</p>
      ) : columns.length === 0 ? (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="admin-db-filter-empty">
          Upload this database's first dataset — its columns will appear here to choose filters from.
        </p>
      ) : (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
          {columns.map((col) => {
            const on = picked.includes(col);
            return (
              <label
                key={col}
                className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs cursor-pointer min-w-0 ${on ? "border-shell-accent bg-shell-accent/5" : "border-border hover:bg-muted/40"}`}
                data-testid={`admin-db-filter-col-${col}`}
              >
                <input type="checkbox" checked={on} onChange={() => toggle(col)} className="mt-0.5 shrink-0" />
                <span className="break-words [overflow-wrap:anywhere]">{columnLabel(col)}</span>
              </label>
            );
          })}
        </div>
      )}
      {columns.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button type="button" onClick={save} disabled={busy || !dirty} className="btn-primary text-xs" data-testid="admin-db-filter-save">
            {busy ? "Saving…" : "Save filters"}
          </button>
          {saved === null && !setupPrompt && (
            <span className="text-[11px] text-muted-foreground">No filters saved yet — the page shows no filters until you save.</span>
          )}
        </div>
      )}
    </section>
  );
}
